import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Briefcase } from 'lucide-react';
import { useCreateJob } from '@/api/queries/recruitment.queries';
import { useDepartments } from '@/api/queries/employee.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';

const EMP_TYPES = ['FULL_TIME','PART_TIME','CONTRACT','DAILY_WAGE'];
const inputCls = "w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all";

function Field({ label, required, error, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[13px] font-semibold text-[var(--text-primary)]">
        {label}{required && <span className="text-[var(--danger)] ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-[12px] text-[var(--danger)]">{error}</p>}
    </div>
  );
}

export default function RecruitmentNewPage() {
  const navigate = useNavigate();
  const createJob = useCreateJob();
  const { data: deptsResp } = useDepartments();
  const departments = deptsResp?.data || [];

  const [form, setForm] = useState({ title: '', department_id: '', location: '', employment_type: 'FULL_TIME', salary_range: '', description: '' });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => ({ ...e, [k]: '' })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.title.trim()) errs.title = 'Required';
    if (!form.location.trim()) errs.location = 'Required';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setServerError('');
    try {
      const dept = departments.find(d => d.id === form.department_id);
      const result = await createJob.mutateAsync({
        ...form,
        title: form.title.trim(),
        department: dept ? { id: dept.id, name: dept.name } : null,
      });
      navigate(result?.data?.id ? `/recruitment/${result.data.id}` : '/recruitment');
    } catch (err) {
      setServerError(err?.response?.data?.error?.message || 'Failed to create job');
    }
  };

  return (
    <div className="max-w-2xl space-y-5 fade-in">
      <PageHeader
        title="Post New Job"
        breadcrumb={[{ label: 'Recruitment', href: '/recruitment' }, { label: 'New Job' }]}
        actions={<button onClick={() => navigate('/recruitment')} className="flex items-center gap-1.5 text-[13px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><ArrowLeft size={15}/> Back</button>}
      />

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="card space-y-4 p-5">
          <div className="flex items-center gap-2 pb-1 border-b border-[var(--border)]">
            <Briefcase size={15} className="text-[var(--accent)]"/>
            <h3 className="text-[13px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">Job Details</h3>
          </div>
          <Field label="Job Title" required error={errors.title}>
            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Senior React Developer" className={inputCls}/>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Department">
              <select value={form.department_id} onChange={e => set('department_id', e.target.value)} className={inputCls}>
                <option value="">Select department</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Employment Type">
              <select value={form.employment_type} onChange={e => set('employment_type', e.target.value)} className={inputCls}>
                {EMP_TYPES.map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Location" required error={errors.location}>
              <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Mumbai (Hybrid)" className={inputCls}/>
            </Field>
            <Field label="Salary Range">
              <input value={form.salary_range} onChange={e => set('salary_range', e.target.value)} placeholder="e.g. ₹12L – ₹18L" className={inputCls}/>
            </Field>
          </div>
          <Field label="Job Description">
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Describe the role, responsibilities, and requirements…"
              rows={5}
              className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] resize-none"/>
          </Field>
        </div>

        {serverError && <div className="px-4 py-3 rounded-[10px] bg-[var(--danger)]/10 border border-[var(--danger)]/30 text-[13px] text-[var(--danger)]">{serverError}</div>}

        <div className="flex gap-3">
          <Button type="submit" loading={createJob.isPending} size="lg" className="flex-1 sm:flex-none sm:px-8">Post Job</Button>
          <Button type="button" variant="outline" size="lg" onClick={() => navigate('/recruitment')}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
